/**
 * Dropdown - Menu with options
 *
 * Usage:
 * <Dropdown
 *   trigger={({ selectedItem }) => (
 *     <Button rightIcon={<ChevronDown />}>
 *       {selectedItem?.label ?? 'Select...'}
 *     </Button>
 *   )}
 *   value="edit"
 *   onSelect={(value) => setValue(value)}
 *   items={[
 *     { value: 'edit', label: 'Edit', icon: <Edit /> },
 *     { value: 'delete', label: 'Delete', variant: 'danger' },
 *   ]}
 * />
 *
 * Action menu (no selection state):
 * <Dropdown
 *   trigger={<IconButton icon={<MoreHorizontal />} label="Options" />}
 *   onSelect={(value) => handleAction(value)}
 *   items={[
 *     { value: 'edit', label: 'Edit' },
 *     { value: 'delete', label: 'Delete', variant: 'danger' },
 *   ]}
 * />
 */

import { Check } from 'lucide-react';
import {
  useState,
  useRef,
  useEffect,
  cloneElement,
  isValidElement,
  useCallback,
  useLayoutEffect,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../utils';

export interface DropdownItem {
  type?: 'item' | 'divider' | 'header';
  /** Unique value for this item, passed to onSelect */
  value?: string;
  label?: string;
  /** Secondary text shown below label (for headers) or beside label (for items) */
  description?: string;
  icon?: React.ReactNode;
  /** Right-aligned icon or element */
  rightIcon?: React.ReactNode;
  /** Keyboard shortcut text displayed on the right */
  shortcut?: string;
  disabled?: boolean;
  variant?: 'default' | 'danger';
  /** For checkbox-style items */
  checkbox?: boolean;
  checked?: boolean;
  /** For toggle switch items */
  toggle?: boolean;
  onToggle?: (checked: boolean) => void;
}

export interface DropdownTriggerProps {
  /** Whether the dropdown is currently open */
  isOpen: boolean;
  /** The currently selected item (if value prop is set) */
  selectedItem: DropdownItem | undefined;
}

export interface DropdownProps {
  /** Element that triggers dropdown - can be a render function for dynamic content */
  trigger: React.ReactElement | ((props: DropdownTriggerProps) => React.ReactElement);
  /** Menu items */
  items: DropdownItem[];
  /** Currently selected value (for select-style dropdowns) */
  value?: string;
  /** Called when an item is selected (receives item.value) */
  onSelect?: (value: string) => void;
  /** Dropdown alignment */
  align?: 'left' | 'right';
  /** Additional classes */
  className?: string;
}

interface MenuPosition {
  top: number;
  left: number;
  width: number;
  openUpward: boolean;
  maxHeight: number;
}

export function Dropdown({
  trigger,
  items,
  value,
  onSelect,
  align = 'left',
  className,
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Find the selected item based on value prop
  const selectedItem = value ? items.find((item) => item.value === value) : undefined;

  // Calculate menu position based on trigger element
  const calculatePosition = useCallback(() => {
    if (!containerRef.current) return;

    const triggerRect = containerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const spaceBelow = viewportHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;

    // Estimate menu height (approximately 40px per item, max 320px)
    const estimatedMenuHeight = Math.min(items.length * 40 + 16, 320);
    // Use trigger width but ensure minimum of 180px
    const menuWidth = Math.max(triggerRect.width, 180);
    const gap = 8; // mt-2 / mb-2

    // Open upward if not enough space below but enough above
    const openUpward = spaceBelow < estimatedMenuHeight && spaceAbove > estimatedMenuHeight;

    const availableSpace = Math.max(0, (openUpward ? spaceAbove : spaceBelow) - gap);
    const maxHeight = Math.min(320, availableSpace);

    // Calculate left position based on alignment
    let left: number;
    if (align === 'right') {
      left = triggerRect.right - menuWidth;
    } else {
      left = triggerRect.left;
    }

    // Keep menu within viewport horizontally
    left = Math.max(8, Math.min(left, viewportWidth - menuWidth - 8));

    // Calculate top position
    const top = openUpward ? triggerRect.top - gap : triggerRect.bottom + gap;

    setMenuPosition({ top, left, width: menuWidth, openUpward, maxHeight });
  }, [items.length, align]);

  // Update position when menu opens or on scroll/resize
  useLayoutEffect(() => {
    if (isOpen) {
      calculatePosition();
    }
  }, [isOpen, calculatePosition]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      // Check if click is outside both trigger and menu (menu is in portal)
      const isOutsideTrigger = containerRef.current && !containerRef.current.contains(target);
      const isOutsideMenu = menuRef.current && !menuRef.current.contains(target);
      if (isOutsideTrigger && isOutsideMenu) {
        setIsOpen(false);
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopImmediatePropagation();
        setIsOpen(false);
      }
    }

    function handleResize() {
      if (isOpen) calculatePosition();
    }

    document.addEventListener('mousedown', handleClickOutside);
    // Use capture phase so this fires before any bubble-phase listeners (e.g., sidebar toggle)
    document.addEventListener('keydown', handleEscape, { capture: true });
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape, { capture: true });
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
    };
  }, [isOpen, calculatePosition]);

  const handleItemClick = (item: DropdownItem) => {
    if (item.value && onSelect) {
      onSelect(item.value);
    }
    setIsOpen(false);
  };

  // Render trigger - support both static elements and render functions
  const renderTrigger = () => {
    const triggerProps: DropdownTriggerProps = { isOpen, selectedItem };

    if (typeof trigger === 'function') {
      return trigger(triggerProps);
    }

    // For static elements, clone and pass props if it's a valid element
    if (isValidElement(trigger)) {
      return cloneElement(trigger);
    }

    return trigger;
  };

  const handleToggle = useCallback(() => {
    if (!isOpen) {
      calculatePosition();
    }
    setIsOpen(!isOpen);
  }, [isOpen, calculatePosition]);

  // Render menu content
  const renderMenu = () => {
    if (!isOpen || !menuPosition) return null;

    const menuContent = (
      <div
        ref={menuRef}
        style={{
          position: 'fixed',
          top: menuPosition.openUpward ? 'auto' : menuPosition.top,
          bottom: menuPosition.openUpward ? window.innerHeight - menuPosition.top + 8 : 'auto',
          left: menuPosition.left,
          width: menuPosition.width,
          maxHeight: menuPosition.maxHeight,
        }}
        className={cn(
          'z-[9999] max-h-[80vh] overflow-y-auto overscroll-contain rounded-lg border border-gray-200 bg-white py-1 shadow-xl dark:border-white/[0.06] dark:bg-[#18181b]',
          className
        )}
      >
        {items.map((item, index) => {
          // Divider
          if (item.type === 'divider') {
            return (
              <div key={index} className="my-1 border-t border-gray-200 dark:border-white/[0.06]" />
            );
          }

          // Header (non-interactive)
          if (item.type === 'header') {
            return (
              <div key={index} className="px-4 py-2">
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  {item.label}
                </div>
                {item.description && (
                  <div className="text-xs text-gray-500 dark:text-zinc-500">{item.description}</div>
                )}
              </div>
            );
          }

          // Toggle item (doesn't close dropdown)
          if (item.toggle) {
            return (
              <button
                key={index}
                type="button"
                onClick={() => {
                  item.onToggle?.(!item.checked);
                }}
                disabled={item.disabled}
                className={cn(
                  'flex w-full items-center justify-between gap-3 px-4 py-2 text-left text-sm transition-colors',
                  'hover:bg-gray-100 dark:hover:bg-white/[0.04]',
                  item.disabled && 'cursor-not-allowed opacity-50',
                  'text-gray-700 hover:text-gray-900 dark:text-zinc-300 dark:hover:text-white'
                )}
              >
                <span className="flex items-center gap-3">
                  {item.icon}
                  {item.label}
                </span>
                <div
                  className={cn(
                    'relative h-5 w-9 rounded-full transition-colors',
                    item.checked ? 'bg-blue-600' : 'bg-gray-300 dark:bg-zinc-600'
                  )}
                >
                  <div
                    className={cn(
                      'absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform',
                      item.checked ? 'translate-x-4' : 'translate-x-0.5'
                    )}
                  />
                </div>
              </button>
            );
          }

          // Checkbox item (doesn't close dropdown)
          if (item.checkbox) {
            return (
              <button
                key={index}
                type="button"
                onClick={() => {
                  item.onToggle?.(!item.checked);
                }}
                disabled={item.disabled}
                className={cn(
                  'flex w-full items-center gap-3 px-4 py-2 text-left text-sm transition-colors',
                  'hover:bg-gray-100 dark:hover:bg-white/[0.04]',
                  item.disabled && 'cursor-not-allowed opacity-50',
                  'text-gray-700 hover:text-gray-900 dark:text-zinc-300 dark:hover:text-white'
                )}
              >
                <div
                  className={cn(
                    'flex h-4 w-4 items-center justify-center rounded border transition-colors',
                    item.checked
                      ? 'border-blue-600 bg-blue-600 text-white'
                      : 'border-gray-300 dark:border-zinc-600'
                  )}
                >
                  {item.checked && <Check className="h-3 w-3" />}
                </div>
                <span className="flex-1">{item.label}</span>
                {item.description && (
                  <span className="text-xs text-gray-500 dark:text-zinc-500">
                    {item.description}
                  </span>
                )}
              </button>
            );
          }

          // Regular item - auto-select based on value prop
          const isSelected = value !== undefined && item.value === value;

          return (
            <button
              key={index}
              type="button"
              onClick={() => handleItemClick(item)}
              disabled={item.disabled}
              className={cn(
                'flex w-full items-center gap-3 px-4 py-2 text-left text-sm transition-colors',
                'hover:bg-gray-100 dark:hover:bg-white/[0.04]',
                item.disabled && 'cursor-not-allowed opacity-50',
                isSelected && 'bg-blue-500/10',
                item.variant === 'danger'
                  ? 'text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300'
                  : 'text-gray-700 hover:text-gray-900 dark:text-zinc-300 dark:hover:text-white'
              )}
            >
              {item.icon && (
                <span
                  className={cn(
                    isSelected
                      ? 'text-blue-600 dark:text-blue-400'
                      : item.variant === 'danger'
                        ? '' // inherit from parent
                        : 'text-gray-500 dark:text-zinc-500'
                  )}
                >
                  {item.icon}
                </span>
              )}
              <span className="flex flex-1 flex-col">
                <span>{item.label}</span>
                {item.description && (
                  <span className="text-xs text-gray-500 dark:text-zinc-500">
                    {item.description}
                  </span>
                )}
              </span>
              {isSelected && <Check className="h-4 w-4 text-blue-600 dark:text-blue-400" />}
              {item.rightIcon}
              {item.shortcut && (
                <span className="text-xs text-gray-400 dark:text-zinc-500">{item.shortcut}</span>
              )}
            </button>
          );
        })}
      </div>
    );

    return createPortal(menuContent, document.body);
  };

  return (
    <div ref={containerRef} className="inline-block">
      <div onClick={handleToggle}>{renderTrigger()}</div>
      {renderMenu()}
    </div>
  );
}
